import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { MODULE_SETTINGS_PATHS } from "./module-settings.paths";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  CompanyExpenses,
  CompanyAllExpensesPage,
  CompanyProjectExpensesPage,
  CompanyAssetExpensesPage,
  CompanyMiscExpensesPage,
  CompanyExpenseReimbursementsPage,
  CompanyExpenseRequestsPage,
  CompanyExpenseDetail,
  CompanyExpenseCreate,
  CompanyExpenseEdit,
  CompanyReimbursementDetail,
  CompanyProjectDetail,
  CompanyExpenseSettingsPage,
} from "@/utils/lazyLoadComponents";

export enum COMPANY_EXPENSES_PATH_SEGMENTS {
  ROOT = "expenses",
  ALL = "all",
  PROJECT = "project",
  ASSET = "asset",
  MISC = "misc",
  REIMBURSEMENTS = "reimbursements",
  REQUESTS = "requests",
  REIMBURSEMENT_DETAIL = "reimbursements/:id",
  PROJECT_DETAIL = "project/:projectId",
  CREATE = "create",
  EDIT = ":id/edit",
  DETAILS = ":id",
  // Module settings (moved out of Company Settings)
  // Literal, not `MODULE_SETTINGS_SEGMENT`: `isolatedModules` forbids enum
  // members initialized from an imported const. Keep both in sync.
  SETTINGS = "settings",
}

/** Query param the create page reads to open pre-set to a project. */
export const EXPENSE_CREATE_PROJECT_PARAM = "projectId";

export const companyExpensesRoutes: RouteObject = {
  path: COMPANY_EXPENSES_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Expenses" },
  HydrateFallback: Spinner,
  children: [
    // ── Page-level tabs layout: each tab owns its own stats + table ──────────
    {
      Component: CompanyExpenses,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(COMPANY_EXPENSES_PATH_SEGMENTS.ALL),
        },
        {
          path: COMPANY_EXPENSES_PATH_SEGMENTS.ALL,
          handle: { breadcrumb: "All Expenses" },
          Component: CompanyAllExpensesPage,
        },
        {
          path: COMPANY_EXPENSES_PATH_SEGMENTS.PROJECT,
          handle: { breadcrumb: "Project Expenses" },
          Component: CompanyProjectExpensesPage,
        },
        {
          path: COMPANY_EXPENSES_PATH_SEGMENTS.ASSET,
          handle: { breadcrumb: "Asset Expenses" },
          Component: CompanyAssetExpensesPage,
        },
        {
          path: COMPANY_EXPENSES_PATH_SEGMENTS.MISC,
          handle: { breadcrumb: "Misc Expenses" },
          Component: CompanyMiscExpensesPage,
        },
        {
          path: COMPANY_EXPENSES_PATH_SEGMENTS.REIMBURSEMENTS,
          handle: { breadcrumb: "Reimbursements" },
          Component: CompanyExpenseReimbursementsPage,
        },
        {
          path: COMPANY_EXPENSES_PATH_SEGMENTS.REQUESTS,
          handle: { breadcrumb: "Expense Requests" },
          Component: CompanyExpenseRequestsPage,
        },
      ],
    },
    // ── Module settings — a SIBLING of the tabs layout, never a child of it:
    // nesting it inside would render it under a tab bar with no matching tab.
    // Static "settings" also outranks any dynamic sibling in React Router's
    // route ranking, so there is no collision. ──────────────────────────────
    {
      path: COMPANY_EXPENSES_PATH_SEGMENTS.SETTINGS,
      handle: { breadcrumb: "Settings", title: "Expenses Settings" },
      Component: CompanyExpenseSettingsPage,
    },
    {
      path: COMPANY_EXPENSES_PATH_SEGMENTS.REIMBURSEMENT_DETAIL,
      handle: { breadcrumb: "Reimbursement" },
      Component: CompanyReimbursementDetail,
    },
    {
      path: COMPANY_EXPENSES_PATH_SEGMENTS.PROJECT_DETAIL,
      handle: { breadcrumb: "Project Budget" },
      Component: CompanyProjectDetail,
    },
    {
      path: COMPANY_EXPENSES_PATH_SEGMENTS.CREATE,
      handle: { breadcrumb: "Submit Expense" },
      Component: CompanyExpenseCreate,
    },
    {
      path: COMPANY_EXPENSES_PATH_SEGMENTS.EDIT,
      handle: { breadcrumb: "Edit Expense" },
      Component: CompanyExpenseEdit,
    },
    {
      path: COMPANY_EXPENSES_PATH_SEGMENTS.DETAILS,
      handle: { breadcrumb: "Expense Details" },
      Component: CompanyExpenseDetail,
    },
  ],
};

export const COMPANY_EXPENSES_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_EXPENSES_PATH_SEGMENTS.ROOT)),
  ALL: withCompanyPrefix(
    addSlashes(
      COMPANY_EXPENSES_PATH_SEGMENTS.ROOT,
      COMPANY_EXPENSES_PATH_SEGMENTS.ALL,
    ),
  ),
  PROJECT: withCompanyPrefix(
    addSlashes(
      COMPANY_EXPENSES_PATH_SEGMENTS.ROOT,
      COMPANY_EXPENSES_PATH_SEGMENTS.PROJECT,
    ),
  ),
  ASSET: withCompanyPrefix(
    addSlashes(
      COMPANY_EXPENSES_PATH_SEGMENTS.ROOT,
      COMPANY_EXPENSES_PATH_SEGMENTS.ASSET,
    ),
  ),
  MISC: withCompanyPrefix(
    addSlashes(
      COMPANY_EXPENSES_PATH_SEGMENTS.ROOT,
      COMPANY_EXPENSES_PATH_SEGMENTS.MISC,
    ),
  ),
  REIMBURSEMENTS: withCompanyPrefix(
    addSlashes(
      COMPANY_EXPENSES_PATH_SEGMENTS.ROOT,
      COMPANY_EXPENSES_PATH_SEGMENTS.REIMBURSEMENTS,
    ),
  ),
  REQUESTS: withCompanyPrefix(
    addSlashes(
      COMPANY_EXPENSES_PATH_SEGMENTS.ROOT,
      COMPANY_EXPENSES_PATH_SEGMENTS.REQUESTS,
    ),
  ),
  CREATE: withCompanyPrefix(
    addSlashes(
      COMPANY_EXPENSES_PATH_SEGMENTS.ROOT,
      COMPANY_EXPENSES_PATH_SEGMENTS.CREATE,
    ),
  ),
  // Submit from a project's detail page: type is fixed to Project and the
  // project pre-selected.
  CREATE_FOR_PROJECT: (projectId: string | number) =>
    `${withCompanyPrefix(
      addSlashes(
        COMPANY_EXPENSES_PATH_SEGMENTS.ROOT,
        COMPANY_EXPENSES_PATH_SEGMENTS.CREATE,
      ),
    )}?${EXPENSE_CREATE_PROJECT_PARAM}=${projectId}`,
  // ── Module settings (resolved in module-settings.paths.ts) ────────────────
  SETTINGS: MODULE_SETTINGS_PATHS.EXPENSES.ROOT,
  SETTINGS_VTAB_CATEGORIES: MODULE_SETTINGS_PATHS.EXPENSES.VTAB_CATEGORIES,

  REIMBURSEMENT_DETAIL: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_EXPENSES_PATH_SEGMENTS.ROOT,
        COMPANY_EXPENSES_PATH_SEGMENTS.REIMBURSEMENTS,
        String(id),
      ),
    ),
  PROJECT_DETAIL: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_EXPENSES_PATH_SEGMENTS.ROOT,
        COMPANY_EXPENSES_PATH_SEGMENTS.PROJECT,
        String(id),
      ),
    ),
  DETAIL: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_EXPENSES_PATH_SEGMENTS.ROOT, String(id)),
    ),
  EDIT: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_EXPENSES_PATH_SEGMENTS.ROOT, String(id), "edit"),
    ),
});
